import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Users } from "lucide-react";
import { EventRegistrations } from "./EventRegistrations";

const EMPTY = { title: "", category: "Çocuk", date: "", start: "", end: "", location: "Bilgece Atölye, Kadıköy", address: "", is_online: false, instructor: "", description: "", capacity: 20, price: 0, min_age: null, max_age: null, target_audience: "", cover: "", registration_deadline: "", status: "published" };
const CATS = ["Çocuk", "Genç", "Ebeveyn", "Öğretmen", "Lider", "Yetişkin", "Kitap & düşünce buluşmaları"];
const STATUSES = [["published", "Yayında"], ["full", "Dolu"], ["draft", "Taslak"], ["cancelled", "İptal"], ["past", "Geçmiş"]];
const inp = "w-full border border-brand-gold-light/50 rounded px-3 py-2 text-sm bg-white outline-none focus:border-brand-gold";

export default function EventManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = () => api.get("/admin/events").then(({ data }) => setItems(data)).catch(() => toast.error("Etkinlikler yüklenemedi."));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/events/${editing.id}`, editing);
      else await api.post("/admin/events", editing);
      toast.success("Etkinlik kaydedildi."); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kayıt başarısız."); }
  };
  const del = async (id) => { if (!window.confirm("Silinsin mi?")) return; await api.delete(`/admin/events/${id}`); toast.success("Silindi."); load(); };

  return (
    <div data-testid="admin-events">
      <div className="flex flex-wrap gap-4 justify-between items-center mb-6">
        <h1 className="font-serif text-4xl text-brand-green">Etkinlikler</h1>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="event-new" className="inline-flex items-center gap-2 bg-brand-gold text-brand-offwhite px-5 py-2.5 rounded-sm text-sm"><Plus size={16} /> Yeni Etkinlik</button>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        {items.map((e) => <EventCard key={e.id} e={e} onEdit={() => setEditing({ ...EMPTY, ...e })} onDel={() => del(e.id)} onView={() => setViewing(e)} />)}
      </div>
      {editing && <EventForm editing={editing} setEditing={setEditing} save={save} />}
      {viewing && <EventRegistrations event={viewing} onClose={() => { setViewing(null); load(); }} />}
    </div>
  );
}

function EventCard({ e, onEdit, onDel, onView }) {
  const s = e.stats || {};
  const cap = e.capacity;
  const used = cap != null ? cap - (e.remaining ?? cap) : (s.confirmed || 0) + (s.pending || 0);
  const pct = cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const label = (STATUSES.find(([v]) => v === e.status) || [e.status, e.status])[1];
  return (
    <div data-testid={`event-row-${e.id}`} className="bg-brand-offwhite border border-brand-gold-light/40 rounded-sm p-6">
      <div className="flex justify-between items-start gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-widest text-brand-gold font-semibold">{e.category}</span>
          <span data-testid={`event-status-${e.id}`} className={`text-[11px] px-2 py-0.5 rounded-full ${e.status === "full" ? "bg-red-100 text-red-800" : e.status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-700"}`}>{label}</span>
        </div>
        <div className="flex gap-3">
          <button onClick={onEdit} data-testid={`event-edit-${e.id}`} className="text-brand-green" aria-label="Düzenle"><Pencil size={16} /></button>
          <button onClick={onDel} data-testid={`event-delete-${e.id}`} className="text-red-700" aria-label="Sil"><Trash2 size={16} /></button>
        </div>
      </div>
      <h3 className="font-serif text-xl text-brand-green mt-2">{e.title}</h3>
      <p className="text-brand-text/60 text-sm mt-1">{e.date} · {e.location} · {e.price > 0 ? `${e.price} ₺` : "Ücretsiz"}</p>
      <div className="mt-4">
        <div className="flex justify-between text-xs text-brand-text/70 mb-1">
          <span data-testid={`event-capacity-${e.id}`}>{cap != null ? `${used} / ${cap} dolu · ${e.remaining ?? cap} boş` : `${used} kayıt · sınırsız`}</span>
          <span>{pct}%</span>
        </div>
        <div className="h-1.5 bg-brand-gold-light/30 rounded-full overflow-hidden"><div className="h-full bg-brand-gold transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
        <div className="flex gap-4 text-xs text-brand-text/70">
          <span data-testid={`event-confirmed-${e.id}`}>Onaylı: <b>{s.confirmed || 0}</b></span>
          <span>Bekleyen ödeme: <b>{s.pending || 0}</b></span>
          <span data-testid={`event-waitlist-${e.id}`}>Bekleme listesi: <b>{s.waitlisted || 0}</b></span>
        </div>
        <button onClick={onView} data-testid={`event-registrations-btn-${e.id}`} className="inline-flex items-center gap-1.5 text-sm text-brand-green border border-brand-gold-light px-3 py-1.5 rounded-sm hover:bg-brand-gold-light/20 transition-colors"><Users size={14} /> Katılımcılar</button>
      </div>
    </div>
  );
}

function EventForm({ editing, setEditing, save }) {
  const set = (k) => (ev) => setEditing({ ...editing, [k]: ev.target.value });
  const num = (k) => (ev) => setEditing({ ...editing, [k]: ev.target.value === "" ? null : Number(ev.target.value) });
  return (
    <div className="fixed inset-0 bg-brand-green/50 flex items-start justify-center z-50 p-4 sm:p-6 overflow-y-auto" onClick={() => setEditing(null)}>
      <div data-testid="event-form-modal" className="bg-brand-offwhite rounded-sm p-6 sm:p-8 max-w-2xl w-full my-8" onClick={(ev) => ev.stopPropagation()}>
        <h3 className="font-serif text-2xl text-brand-green mb-5">{editing.id ? "Etkinliği Düzenle" : "Yeni Etkinlik"}</h3>
        <div className="space-y-4">
          <F l="Başlık"><input data-testid="event-field-title" value={editing.title} onChange={set("title")} className={inp} /></F>
          <div className="grid sm:grid-cols-3 gap-4">
            <F l="Kategori"><select data-testid="event-field-category" value={editing.category} onChange={set("category")} className={inp}>{CATS.map((c) => <option key={c}>{c}</option>)}</select></F>
            <F l="Tarih"><input type="date" data-testid="event-field-date" value={editing.date} onChange={set("date")} className={inp} /></F>
            <F l="Son kayıt tarihi"><input type="date" data-testid="event-field-deadline" value={editing.registration_deadline || ""} onChange={set("registration_deadline")} className={inp} /></F>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <F l="Başlangıç"><input type="datetime-local" data-testid="event-field-start" value={editing.start || ""} onChange={set("start")} className={inp} /></F>
            <F l="Bitiş"><input type="datetime-local" data-testid="event-field-end" value={editing.end || ""} onChange={set("end")} className={inp} /></F>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <F l="Lokasyon"><input data-testid="event-field-location" value={editing.location} onChange={set("location")} className={inp} /></F>
            <F l="Eğitmen"><input data-testid="event-field-instructor" value={editing.instructor || ""} onChange={set("instructor")} className={inp} /></F>
          </div>
          <F l="Adres"><input data-testid="event-field-address" value={editing.address || ""} onChange={set("address")} className={inp} /></F>
          <F l="Kapak görseli URL"><input data-testid="event-field-cover" value={editing.cover || ""} onChange={set("cover")} className={inp} /></F>
          <F l="Açıklama"><textarea data-testid="event-field-description" value={editing.description} onChange={set("description")} className={inp} rows={3} /></F>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <F l="Kapasite"><input type="number" min="0" data-testid="event-field-capacity" value={editing.capacity ?? ""} onChange={num("capacity")} className={inp} /></F>
            <F l="Ücret (₺)"><input type="number" min="0" data-testid="event-field-price" value={editing.price ?? 0} onChange={(ev) => setEditing({ ...editing, price: Number(ev.target.value) || 0 })} className={inp} /></F>
            <F l="Min yaş"><input type="number" data-testid="event-field-min-age" value={editing.min_age ?? ""} onChange={num("min_age")} className={inp} /></F>
            <F l="Maks yaş"><input type="number" data-testid="event-field-max-age" value={editing.max_age ?? ""} onChange={num("max_age")} className={inp} /></F>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <F l="Hedef kitle"><input data-testid="event-field-audience" value={editing.target_audience || ""} onChange={set("target_audience")} className={inp} /></F>
            <F l="Durum"><select data-testid="event-field-status" value={editing.status} onChange={set("status")} className={inp}>{STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></F>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="event-field-online" checked={!!editing.is_online} onChange={(ev) => setEditing({ ...editing, is_online: ev.target.checked })} /> Online etkinlik</label>
          {editing.id && <p className="text-xs text-brand-text/60">Kapasiteyi değiştirdiğinizde boş kontenjan mevcut onaylı kayıtlara göre yeniden hesaplanır.</p>}
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={save} data-testid="event-save" className="bg-brand-gold text-brand-offwhite px-6 py-2.5 rounded-sm text-sm">Kaydet</button>
          <button onClick={() => setEditing(null)} data-testid="event-cancel" className="border border-brand-gold-light px-6 py-2.5 rounded-sm text-sm">İptal</button>
        </div>
      </div>
    </div>
  );
}
function F({ l, children }) { return <div><label className="block text-sm font-medium text-brand-text mb-1">{l}</label>{children}</div>; }
